import { io } from "socket.io-client";
import { API_BASE } from "../api/api";

let channelChatSocket = null;
const inflightChannelJoins = new Map();
const recentChannelJoins = new Map();
const RECENT_JOIN_TTL_MS = 5000;

const nowIso = () => new Date().toISOString();

const logChannelSocket = (event, payload = {}) => {
  console.log(`[channel-chat][frontend][${nowIso()}] ${event}`, payload);
};

export function getChannelChatSocket({ token } = {}) {
  if (channelChatSocket && channelChatSocket.connected) {
    logChannelSocket("socket.reuse", {
      socketId: channelChatSocket.id,
      connected: channelChatSocket.connected,
    });
    return channelChatSocket;
  }

  if (!channelChatSocket) {
    logChannelSocket("socket.create", {
      apiBase: API_BASE,
      hasToken: Boolean(token),
    });
    channelChatSocket = io(API_BASE, {
      transports: ["websocket"],
      withCredentials: true,
      auth: token ? { token } : undefined,
    });
    channelChatSocket.on("connect", () => {
      logChannelSocket("socket.connect", { socketId: channelChatSocket?.id });
    });
    channelChatSocket.on("disconnect", (reason) => {
      logChannelSocket("socket.disconnect", { reason });
    });
    channelChatSocket.on("connect_error", (error) => {
      logChannelSocket("socket.connect_error", {
        message: error?.message || String(error),
      });
    });
  } else if (!channelChatSocket.connected) {
    logChannelSocket("socket.reconnect", {
      hasToken: Boolean(token),
    });
    channelChatSocket.auth = token ? { token } : undefined;
    channelChatSocket.connect();
  }

  return channelChatSocket;
}

function buildJoinKey(payload = {}) {
  return JSON.stringify({
    userId: payload?.userId || payload?.user_id || null,
    channelId: payload?.channelId || payload?.channel_id || null,
    sessionId: payload?.sessionId || null,
    chatContextKey: payload?.chatContext?.key || null,
    chatContextType: payload?.chatContext?.type || null,
  });
}

export function joinChannelChatSession({ token, payload }) {
  const socket = getChannelChatSocket({ token });
  const joinKey = buildJoinKey(payload);
  const now = Date.now();
  const recent = recentChannelJoins.get(joinKey);

  if (recent && now - recent.timestamp < RECENT_JOIN_TTL_MS) {
    logChannelSocket("join.skip_recent", {
      joinKey,
      sessionId: payload?.sessionId || null,
      channelId: payload?.channelId || payload?.channel_id || null,
      ageMs: now - recent.timestamp,
    });
    return Promise.resolve(recent.response);
  }

  const existing = inflightChannelJoins.get(joinKey);
  if (existing) {
    logChannelSocket("join.reuse_inflight", {
      joinKey,
      sessionId: payload?.sessionId || null,
      channelId: payload?.channelId || payload?.channel_id || null,
    });
    return existing;
  }

  const request = new Promise((resolve, reject) => {
    socket.emit("channel-chat:join-session", payload, (response) => {
      inflightChannelJoins.delete(joinKey);
      if (response?.ok) {
        recentChannelJoins.set(joinKey, {
          timestamp: Date.now(),
          response,
        });
        resolve(response);
        return;
      }
      reject(new Error(response?.error || "Failed to join channel session"));
    });
  });

  inflightChannelJoins.set(joinKey, request);
  return request;
}

export function disconnectChannelChatSocket() {
  if (channelChatSocket) {
    logChannelSocket("socket.manual_disconnect", {
      socketId: channelChatSocket.id,
    });
    channelChatSocket.disconnect();
    channelChatSocket = null;
  }
  inflightChannelJoins.clear();
  recentChannelJoins.clear();
}
